/**
 * Domain types for the League setup tab (Seasons / Regions / Levels / Custom
 * tags). The dashboard REST DTOs (`LeagueSeason`, `LeagueRegion`, …) are mapped
 * into these before reaching the UI, so components never depend on generated
 * schema types. Ids are opaque public ids (`sea_…` / `reg_…` / `lvl_…` /
 * `tag_…`); dates are `YYYY-MM-DD` strings.
 */

/** A lightweight reference to another league-setup entity (season/region/level). */
export type LeagueSetupRef = {
  id: string;
  title: string;
};

export type LeagueSeason = {
  id: string;
  title: string;
  /** Start date, `YYYY-MM-DD`. */
  periodStart: string;
  /** End date, `YYYY-MM-DD`. */
  periodEnd: string;
};

export type LeagueRegion = {
  id: string;
  title: string;
  /** Seasons this region applies to. Empty ⇒ applies to all seasons. */
  seasons: LeagueSetupRef[];
};

export type LeagueLevel = {
  id: string;
  title: string;
  /** Seasons this level applies to. Empty ⇒ applies to all seasons. */
  seasons: LeagueSetupRef[];
  /** Regions this level applies to. Empty ⇒ applies to all regions. */
  regions: LeagueSetupRef[];
};

export type LeagueCustomTag = {
  id: string;
  title: string;
  /** Seasons this tag applies to. Empty ⇒ applies to all seasons. */
  seasons: LeagueSetupRef[];
  /** Regions this tag applies to. Empty ⇒ applies to all regions. */
  regions: LeagueSetupRef[];
  /** Levels this tag applies to. Empty ⇒ applies to all levels. */
  levels: LeagueSetupRef[];
};

/** The four resources managed by the League setup tab. */
export const LeagueSetupResourceValues = ["seasons", "regions", "levels", "customTags"] as const;
export type LeagueSetupResource = (typeof LeagueSetupResourceValues)[number];

/**
 * A league-setup filter selection: the ids picked per dimension. Each array is a
 * set of public ids (`sea_…` / `reg_…` / `lvl_…` / `tag_…`); an EMPTY array means
 * "no filter on that dimension" (matches everything). Used both as the league's
 * persisted global default and as the per-page (URL-backed) result filter.
 */
export type LeagueSetupFilterValue = {
  seasonIds: string[];
  regionIds: string[];
  levelIds: string[];
  customTagIds: string[];
};

/** An empty league-setup filter selection (no dimension filtered). */
export const EMPTY_LEAGUE_SETUP_FILTER_VALUE: LeagueSetupFilterValue = {
  seasonIds: [],
  regionIds: [],
  levelIds: [],
  customTagIds: [],
};
